import { Paperclip } from '@phosphor-icons/react';
import { esImagen, getFileUrl, type TicketFile } from '../services/api';
import { AuthedImage } from './AuthedImage';
import styles from './AdjuntosMensaje.module.css';

/** Tamaño legible. Base 1024 y un decimal solo por debajo de 10, para que la
 *  columna no baile: «980 KB», «1.2 MB», «14 MB». Vivía en `Incidencias.tsx`;
 *  desde el paso 34 lo usa también el portal. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/**
 * Los adjuntos de UN mensaje (o del alta), dentro de su burbuja: las capturas
 * en miniatura y los ficheros como botones de descarga. Lo usan el detalle del
 * panel y el portal, que enseñan lo mismo con el mismo gesto.
 */
interface Props {
  incidenciaId: string;
  token: string;
  files: TicketFile[];
  onAbrirImagen: (f: TicketFile) => void;
  onDescargar: (f: TicketFile) => void;
}

export function AdjuntosMensaje({ incidenciaId, token, files, onAbrirImagen, onDescargar }: Props) {
  if (files.length === 0) return null;
  const imagenes = files.filter(esImagen);
  const ficheros = files.filter((f) => !esImagen(f));
  return (
    <div className={styles.wrap}>
      {imagenes.length > 0 && (
        <div className={styles.thumbs}>
          {imagenes.map((f) => (
            <button
              key={f.id}
              type="button"
              className={styles.thumb}
              onClick={() => onAbrirImagen(f)}
              aria-label={`Ampliar ${f.filename}`}
              title={`${f.filename} · ${formatBytes(f.sizeBytes)}`}
            >
              <AuthedImage
                src={getFileUrl(incidenciaId, f.id)}
                type={f.mediaType}
                token={token}
                alt=""
                className={styles.thumbImg}
              />
            </button>
          ))}
        </div>
      )}
      {ficheros.length > 0 && (
        <div className={styles.files}>
          {ficheros.map((f) => (
            <button
              key={f.id}
              type="button"
              className={styles.file}
              onClick={() => onDescargar(f)}
              title={`Descargar ${f.filename} · ${formatBytes(f.sizeBytes)}`}
            >
              <Paperclip size={13} weight="regular" />
              <span className={styles.fileName}>{f.filename}</span>
              <span className={styles.fileSize}>{formatBytes(f.sizeBytes)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
